<template>
  <div 
    class="file-upload"
    :class="{ 'dragging': isDragging }"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent
    @drop.prevent="handleDrop"
    @click="triggerFileInput"
  >
    <input
      ref="fileInput"
      type="file"
      @change="handleFileSelect"
      accept="video/*"
      style="display: none"
    >
    
    <div v-if="!isProcessing && !error && !videoFile" class="upload-placeholder">
      <div class="upload-icon">
        <font-awesome-icon icon="upload" size="2x" />
      </div>
      <div class="upload-text">
        Drop a video file here or click to select
      </div>
    </div>

    <div v-else-if="error" class="upload-error">
      <div class="error-icon">
        <font-awesome-icon icon="exclamation-circle" size="2x" />
      </div>
      <div class="error-message">{{ error }}</div>
      <button class="retry-button" @click="clearError">Try Again</button>
    </div>

    <div v-else-if="isProcessing" class="processing-message">
      <font-awesome-icon icon="spinner" spin size="2x" />
      <div>Analyzing video file...</div>
    </div>

    <div v-else-if="videoFile" class="video-file-info">
      <!-- Video File Status Display -->
      <div class="video-file-status">
        <div class="status-header">
          <font-awesome-icon icon="file-video" size="lg" />
          <span class="file-name" :title="videoFile.file_name">{{ videoFile.file_name }}</span>
        </div>
        <div class="status-details">
          <span>{{ videoFile.file_format || 'Unknown Format' }}</span>
        </div>
        <div class="status-details">
          <div v-if="videoFile.duration" class="detail-item" title="Duration">
            <font-awesome-icon icon="clock" size="sm" />
            <span>{{ formatDuration(videoFile.duration) || 'Unknown' }}</span>
          </div>
          <div v-if="videoFile.file_resolution" class="detail-item" title="Resolution">
            <font-awesome-icon icon="expand" size="sm" />
            <span>{{ videoFile.file_resolution }}</span>
          </div>
          <div v-if="videoFile.file_framerate" class="detail-item" title="Framerate">
            <font-awesome-icon icon="tachometer-alt" size="sm" />
            <span>{{ videoFile.file_framerate }} fps</span>
          </div>
          <div v-if="videoFile.file_format" class="detail-item" title="Codec">
            <font-awesome-icon icon="cube" size="sm" />
            <span>{{ videoFile.file_format }}</span>
          </div>
          <div v-else-if="videoFile.file_codec_id" class="detail-item" title="Codec">
            <font-awesome-icon icon="cube" size="sm" />
            <span>{{ videoFile.file_codec_id }}</span>
          </div>
          <div v-if="videoFile.file_size" class="detail-item" title="Size">
            <font-awesome-icon icon="file" size="sm" />
            <span>{{ formatFileSize(videoFile.file_size) }}</span>
          </div>
          <div v-if="videoFile.file_bit_rate" class="detail-item" title="Bitrate">
            <font-awesome-icon icon="chart-line" size="sm" />
            <span>{{ formatBitrate(videoFile.file_bit_rate) }}</span>
          </div>
          <div v-if="videoFile.file_bit_depth > 8" class="detail-item" title="Bit Depth">
            <span class="label hdr">HDR</span>
            <span>({{ videoFile.file_bit_depth }}-bit)</span>
          </div>
          <div v-if="videoFile.file_encoding_date" class="detail-item" title="Encoding Date">
            <font-awesome-icon icon="calendar" size="sm" />
            <span>Encoding Date: {{ videoFile.file_encoding_date }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { FontAwesomeIcon } from '@/scripts/icons';
import { formatDuration, formatFileSize, formatBitrate } from '@/scripts/shared-utils';
import { isVideoFile, extractVideoMetadata } from '@/scripts/shared-mediainfo';

const isDragging = ref(false);
const isProcessing = ref(false);
const fileInput = ref(null);
const error = ref('');
const videoFile = ref(null);

const triggerFileInput = () => {
  if (videoFile.value) {
    clearFile();
  } else {
    fileInput.value.click();
  }
};

const handleDragEnter = () => {
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const handleDrop = (e) => {
  isDragging.value = false;
  const file = e.dataTransfer.files[0];
  if (file) {
    processFile(file);
  }
};

const handleFileSelect = (e) => {
  const file = e.target.files[0];
  if (file) {
    processFile(file);
  }
};

const clearError = () => {
  error.value = '';
};

const clearFile = () => {
  videoFile.value = null;
  error.value = '';
  
  // Clear the file input
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const processFile = async (file) => {
  // Only process video files
  if (!isVideoFile(file)) {
    error.value = 'Please select a video file';
    return;
  }

  isProcessing.value = true;
  error.value = '';
  videoFile.value = null;

  try {
    // Extract video metadata
    const metadata = await extractVideoMetadata(file);
    
    if (Object.keys(metadata).length === 0) {
      error.value = 'Could not extract metadata from this video file';
      isProcessing.value = false;
      return;
    }

    // Create a video file object with the metadata
    videoFile.value = {
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
      ...metadata
    };
    
    console.debug('Extracted video metadata:', videoFile.value);
  } catch (err) {
    console.error('Error processing video file:', err);
    error.value = err.message || 'An error occurred while analyzing the video file';
  } finally {
    isProcessing.value = false;
  }
};
</script>

<style scoped>
@import '@/styles/file-upload.css';
@import '@/styles/video-file.css';

.video-file-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.processing-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #4a90e2;
}
</style>
